import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Calendar, MessageSquare, Megaphone, AlertCircle, CheckCircle } from 'lucide-react';
import { ColumnSelect } from '../ui/select';
import { PrepaidPlanSection } from './PrepaidPlanSection';
import { parseUtcDate, trialDurationLabel } from '@/hooks/usePlanStatus';

interface TrialOffer {
    key: string;
    code: string;
    days: number;
    message_limit: number;
    active: boolean;
}

interface RecurringSubscription {
    plan_display_name: string;
    status: string;
    next_billing_date: string | null;
    payment_method_masked: string | null;
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
    active: 'Activa',
    past_due: 'Pago fallido, reintentando',
    pending_first_payment: 'Primer cobro en proceso',
};

const CANCELLABLE_SUBSCRIPTION_STATUSES = ['active', 'past_due'];

interface PlanOption {
    label: string;
    plan: string;
    trialOfferKey?: string;
}

const FIXED_PLAN_OPTIONS: PlanOption[] = [
    { label: "Básico (200/mes)", plan: "basic_200" },
    { label: "Estándar (1,000/mes)", plan: "standard_1000" },
    { label: "Premium (3,000/mes)", plan: "premium_3000" },
    { label: "Ilimitado", plan: "unlimited" },
    { label: "Plan Prepago", plan: "prepaid" },
    { label: "Bloqueado", plan: "blocked" },
    { label: "🧪 Pruebas (5/mes)", plan: "test_5" },
];

const trialOfferOption = (offer: TrialOffer): PlanOption => ({
    label: `Free Trial · ${trialDurationLabel(offer.days)} · ${offer.message_limit} msgs (${offer.code})`,
    plan: "free_trial",
    trialOfferKey: offer.key,
});

const formatDateTime = (date: Date) => date.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
});

interface UserDetailModalProps {
    userId: string | null;
    isOpen: boolean;
    onClose: () => void;
    onUserUpdated?: () => void;
}

interface UserDetails {
    user_id: string;
    cycle: {
        start: string;
        end: string;
        usage: number;
        lifetime_total: number;
        lifetime_delivered: number;
        lifetime_failed: number;
        lifetime_pending?: number;
    };
    history: { period: string; count: number }[];
    campaigns: Record<string, number>;
    billing_cycles: Record<string, {
        start: string;
        end: string;
        messages: Record<string, number>;
        campaigns: Record<string, number>;
    }>;
    plan_info?: {
        plan: string;
        billing_cycle_index: number;
        billing_cycle_start: string;
        billing_cycle_end: string;
        limit: number | null;
        usage: number;
        remaining: number | null;
        is_blocked: boolean;
        admin_blocked?: boolean;
        plan_before_block?: string | null;
        prepaid?: Record<string, unknown> | null;
        trial_expired: boolean;
        subscription_expired: boolean;
        plan_changed_at: string | null;
        trial_offer?: string | null;
        trial_days?: number | null;
        trial_ends_at?: string;
        subscription_periods?: Array<{
            plan: string;
            plan_display: string;
            start_date: string;
            end_date: string;
            limit: number | null;
            usage: number;
            remaining: number | null;
        }>;
        prepaid_info?: {
            balance_amount: number;
            price_per_message: number;
            message_limit: number;
            status: string;
            activated_at: string;
        };
    };
    recurring_subscription?: RecurringSubscription | null;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({ userId, isOpen, onClose, onUserUpdated }) => {
    const [details, setDetails] = useState<UserDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<string>("all");
    const [planUpdating, setPlanUpdating] = useState(false);
    const [planError, setPlanError] = useState<string | null>(null);
    const [showPrepaidSetup, setShowPrepaidSetup] = useState(false);
    const [trialOffers, setTrialOffers] = useState<TrialOffer[]>([]);
    const [confirmCancelSubscription, setConfirmCancelSubscription] = useState(false);

    useEffect(() => {
        if (isOpen && userId) {
            fetchDetails();
            setSelectedMonth("all");
            setPlanError(null);
            setShowPrepaidSetup(false);
            setConfirmCancelSubscription(false);
        } else {
            setDetails(null);
        }
    }, [isOpen, userId]);

    useEffect(() => {
        if (!isOpen) return;
        axios.get<TrialOffer[]>('/admin/trial-offers')
            .then((res) => setTrialOffers(res.data))
            .catch((err) => console.error("Failed to fetch trial offers:", err));
    }, [isOpen]);

    const fetchDetails = async () => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get<UserDetails>(`/admin/users/${userId}/details`);
            setDetails(res.data);
        } catch (err) {
            console.error("Failed to fetch user details:", err);
            setError("No se pudieron cargar los detalles del usuario.");
        } finally {
            setLoading(false);
        }
    };

    const handlePlanChange = async (newPlan: string, trialOfferKey?: string) => {
        if (!userId) return;
        setPlanError(null);
        // Prepaid needs a balance: open the prepaid section instead of PUT /plan
        if (newPlan === 'prepaid') {
            setShowPrepaidSetup(true);
            return;
        }
        setPlanUpdating(true);
        try {
            await axios.put(`/admin/users/${userId}/plan`, { plan: newPlan, trial_offer_key: trialOfferKey });
            setShowPrepaidSetup(false);
            await fetchDetails(); // Refresh details in modal
            if (onUserUpdated) onUserUpdated(); // Refresh parent dashboard
        } catch (err: any) {
            console.error("Failed to update plan:", err);
            setPlanError(err?.response?.data?.detail || "No se pudo actualizar el plan.");
        } finally {
            setPlanUpdating(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!userId) return;
        setPlanError(null);
        setPlanUpdating(true);
        try {
            await axios.post(`/admin/users/${userId}/subscription/cancel`);
            setConfirmCancelSubscription(false);
            await fetchDetails();
            if (onUserUpdated) onUserUpdated();
        } catch (err: any) {
            console.error("Failed to cancel subscription:", err);
            setPlanError(err?.response?.data?.detail || "No se pudo cancelar la suscripción.");
        } finally {
            setPlanUpdating(false);
        }
    };

    const handleUnblock = async () => {
        if (!userId) return;
        setPlanError(null);
        setPlanUpdating(true);
        try {
            await axios.post(`/admin/users/${userId}/unblock`);
            await fetchDetails();
            if (onUserUpdated) onUserUpdated();
        } catch (err: any) {
            console.error("Failed to unblock user:", err);
            setPlanError(err?.response?.data?.detail || "No se pudo desbloquear la cuenta.");
        } finally {
            setPlanUpdating(false);
        }
    };

    if (!isOpen) return null;

    // --- Plan selector: one entry per active trial offer plus the fixed plans ---
    const planInfo = details?.plan_info;
    const trialOptions = trialOffers.map(trialOfferOption);
    let currentPlanOption = FIXED_PLAN_OPTIONS.find((opt) => opt.plan === planInfo?.plan);
    if (planInfo?.plan === 'free_trial') {
        // Signup default, legacy (1 month) or deactivated offers stay visible for this user but cannot be re-assigned.
        currentPlanOption = trialOptions.find((opt) => opt.trialOfferKey === planInfo.trial_offer)
            ?? { label: `Free Trial · ${trialDurationLabel(planInfo.trial_days)}`, plan: 'free_trial' };
    }
    const planOptions = [
        ...trialOptions,
        ...(currentPlanOption && !currentPlanOption.trialOfferKey && currentPlanOption.plan === 'free_trial'
            ? [currentPlanOption]
            : []),
        ...FIXED_PLAN_OPTIONS,
    ];
    const handlePlanSelect = (label: string) => {
        const option = planOptions.find((opt) => opt.label === label);
        if (!option || (option.plan === 'free_trial' && !option.trialOfferKey)) return;
        handlePlanChange(option.plan, option.trialOfferKey);
    };

    // --- Helper Logic for Dynamic Data ---
    let displayMessages = {
        total: details?.cycle.lifetime_total || 0,
        delivered: details?.cycle.lifetime_delivered || 0,
        failed: details?.cycle.lifetime_failed || 0,
        pending: details?.cycle.lifetime_pending || 0
    };

    let displayCampaigns = details?.campaigns || {};

    // Override if filtered
    let currentCycleData = null;
    if (selectedMonth !== "all" && details?.billing_cycles?.[selectedMonth]) {
        currentCycleData = details.billing_cycles[selectedMonth];
        const mData = currentCycleData;
        displayMessages = {
            total: mData.messages.total || 0,
            delivered: mData.messages.delivered || 0,
            failed: mData.messages.failed || 0,
            pending: mData.messages.pending || 0
        };
        displayCampaigns = mData.campaigns || {};
    }

    const totalLifetime = displayMessages.total;
    const percentDelivered = totalLifetime > 0 ? (displayMessages.delivered / totalLifetime) * 100 : 0;
    const percentFailed = totalLifetime > 0 ? (displayMessages.failed / totalLifetime) * 100 : 0;
    const percentPending = totalLifetime > 0 ? (displayMessages.pending / totalLifetime) * 100 : 0;

    // Calculate dynamic labels and dates
    let dateLabelStart = "Creación";
    let dateLabelEnd = "Actualidad";
    let dateValueStart = details?.cycle.start ? new Date(details.cycle.start).toLocaleDateString() : "-";
    let dateValueEnd = details?.cycle.end ? new Date(details.cycle.end).toLocaleDateString() : "-";

    if (selectedMonth !== "all" && currentCycleData) {
        dateLabelStart = "Desde";
        dateLabelEnd = "Hasta";
        // Parse ISO strings from backend
        // Billing cycles come with "start" and "end" fields
        dateValueStart = new Date(currentCycleData.start).toLocaleDateString();
        dateValueEnd = new Date(currentCycleData.end).toLocaleDateString();
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-transparent flex justify-between items-center">
                        <div>
                            <h3 className="text-xl leading-6 font-bold text-white shadow-sm" id="modal-title">
                                Reporte Detallado
                            </h3>
                        </div>

                        <button onClick={onClose} className="rounded-md text-white hover:text-gray-200 focus:outline-none bg-white/10 p-1 hover:bg-white/20 transition-colors">
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="px-6 py-6 min-h-[300px]">
                        {loading ? (
                            <div className="flex justify-center items-center h-full py-20">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            </div>
                        ) : error ? (
                            <div className="text-center text-red-500 py-10">{error}</div>
                        ) : details ? (
                            <div className="space-y-8">

                                {/* 0. Plan Status Card */}
                                {details.plan_info && (
                                    <>
                                    <div className={`rounded-xl p-5 border ${details.plan_info.is_blocked || details.plan_info.trial_expired ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-200'}`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <AlertCircle className={`h-5 w-5 ${details.plan_info.is_blocked || details.plan_info.trial_expired ? 'text-red-600' : 'text-indigo-600'}`} />
                                                <h4 className="font-semibold text-gray-900">
                                                    Estado del Plan
                                                </h4>
                                            </div>

                                            {/* Plan Selector */}
                                            <div style={{ width: '280px' }}>
                                                <ColumnSelect
                                                    options={planOptions.map((opt) => opt.label)}
                                                    value={currentPlanOption?.label ?? null}
                                                    onChange={handlePlanSelect}
                                                    disabled={planUpdating}
                                                    placeholder="Selecciona Plan"
                                                />
                                            </div>
                                        </div>

                                        {details.plan_info.admin_blocked && (
                                            <div className="flex items-center justify-between gap-3 mb-4 p-3 bg-white border border-red-200 rounded-lg">
                                                <p className="text-sm text-red-700">
                                                    Cuenta bloqueada por un administrador. Las cuotas pagadas se conservan.
                                                </p>
                                                <button
                                                    onClick={handleUnblock}
                                                    disabled={planUpdating}
                                                    className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                                >
                                                    Desbloquear (restaurar plan anterior)
                                                </button>
                                            </div>
                                        )}

                                        {details.recurring_subscription && (
                                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-white border border-indigo-200 rounded-lg">
                                                <div className="text-sm text-gray-700">
                                                    <p className="font-semibold text-gray-900">
                                                        Suscripción con tarjeta: {details.recurring_subscription.plan_display_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {SUBSCRIPTION_STATUS_LABELS[details.recurring_subscription.status] ?? details.recurring_subscription.status}
                                                        {details.recurring_subscription.next_billing_date &&
                                                            ` · Próximo cobro: ${formatDateTime(parseUtcDate(details.recurring_subscription.next_billing_date))}`}
                                                        {details.recurring_subscription.payment_method_masked &&
                                                            ` · Tarjeta ${details.recurring_subscription.payment_method_masked}`}
                                                    </p>
                                                </div>
                                                {CANCELLABLE_SUBSCRIPTION_STATUSES.includes(details.recurring_subscription.status) && (
                                                    confirmCancelSubscription ? (
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <span className="text-xs text-gray-600">No se volverá a cobrar; conserva el mes pagado y la tarjeta.</span>
                                                            <button
                                                                onClick={handleCancelSubscription}
                                                                disabled={planUpdating}
                                                                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                Sí, cancelar
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmCancelSubscription(false)}
                                                                disabled={planUpdating}
                                                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                                            >
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setConfirmCancelSubscription(true)}
                                                            disabled={planUpdating}
                                                            className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                                                        >
                                                            Cancelar suscripción
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}

                                        {planError && (
                                            <p className="text-sm text-red-600 mb-4">{planError}</p>
                                        )}

                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-sm mb-4">
                                            <div className="bg-white rounded-lg p-2 border">
                                                <div className="font-bold text-lg text-indigo-600">{details.plan_info.billing_cycle_index}</div>
                                                <div className="text-gray-500 text-xs">Ciclo Actual</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-2 border">
                                                <div className="font-bold text-lg text-green-600">{details.plan_info.usage}</div>
                                                <div className="text-gray-500 text-xs">Usados</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-2 border">
                                                <div className={`font-bold text-lg ${details.plan_info.remaining === null ? 'text-green-600' : details.plan_info.remaining <= 0 ? 'text-red-600' : 'text-indigo-600'}`}>
                                                    {details.plan_info.remaining === null ? '∞' : details.plan_info.remaining}
                                                </div>
                                                <div className="text-gray-500 text-xs">Disponibles</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-2 border">
                                                <div className="font-bold text-lg text-gray-700">{details.plan_info.limit === null ? '∞' : details.plan_info.limit}</div>
                                                <div className="text-gray-500 text-xs">Límite</div>
                                            </div>
                                        </div>

                                        {/* Usage Progress Bar (only for limited plans) */}
                                        {details.plan_info.limit !== null && (
                                            <div className="mt-2">
                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full transition-all ${details.plan_info.remaining <= 0 ? 'bg-red-500' : details.plan_info.remaining < 100 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                        style={{ width: `${Math.min(100, (details.plan_info.usage / details.plan_info.limit) * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Información del Ciclo Actual */}
                                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Calendar className="h-4 w-4 text-blue-600" />
                                                <span className="text-sm font-semibold text-blue-900">Periodo Actual</span>
                                            </div>
                                            <div className="text-xs text-blue-700">
                                                <div className="flex justify-between">
                                                    <span>Inicio:</span>
                                                    <span className="font-medium">
                                                        {new Date(details.plan_info.billing_cycle_start).toLocaleString('es-ES', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between mt-1">
                                                    <span>Fin:</span>
                                                    <span className="font-medium">
                                                        {new Date(details.plan_info.billing_cycle_end).toLocaleString('es-ES', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                                {details.plan_info.plan_changed_at && (
                                                    <div className="flex justify-between mt-1">
                                                        <span>Cambio de plan:</span>
                                                        <span className="font-medium">
                                                            {new Date(details.plan_info.plan_changed_at).toLocaleString('es-ES', {
                                                                day: 'numeric',
                                                                month: 'long',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                )}
                                                {details.plan_info.trial_ends_at && (
                                                    <div className="flex justify-between mt-1">
                                                        <span>Fin de la prueba ({trialDurationLabel(details.plan_info.trial_days)}):</span>
                                                        <span className="font-medium">
                                                            {formatDateTime(parseUtcDate(details.plan_info.trial_ends_at))}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {details.plan_info.trial_expired && (
                                            <p className="text-red-600 text-sm mt-3 font-medium">
                                                ⚠️ El periodo de prueba de {trialDurationLabel(details.plan_info.trial_days)} terminó. Cambia a un plan pago para continuar.
                                            </p>
                                        )}
                                        {details.plan_info.subscription_expired && (
                                            <p className="text-red-600 text-sm mt-3 font-medium">
                                                ⚠️ La suscripción ha expirado. Renueva el plan para continuar enviando mensajes.
                                            </p>
                                        )}
                                        {details.plan_info.is_blocked && !details.plan_info.admin_blocked && !details.plan_info.trial_expired && !details.plan_info.subscription_expired && (
                                            <p className="text-red-600 text-sm mt-3 font-medium">
                                                ⚠️ Ha alcanzado el límite de mensajes para este ciclo.
                                            </p>
                                        )}
                                    </div>

                                    {/* Prepaid Plan Section */}
                                    {(details.plan_info.plan === 'prepaid' || !!details.plan_info.prepaid || showPrepaidSetup) && (
                                        <PrepaidPlanSection
                                            userId={userId!}
                                            planInfo={details.plan_info}
                                            onPlanUpdated={() => {
                                                setShowPrepaidSetup(false);
                                                fetchDetails();
                                                if (onUserUpdated) onUserUpdated();
                                            }}
                                        />
                                    )}
                                    </>
                                )}

                                {/* Historial de Periodos Anteriores */}
                                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-5 w-5 text-indigo-600" />
                                            <h4 className="font-semibold text-gray-900">Historial de Periodos</h4>
                                        </div>
                                        {details.plan_info?.subscription_periods && details.plan_info.subscription_periods.length > 0 && (
                                            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                                                {details.plan_info.subscription_periods.length} {details.plan_info.subscription_periods.length === 1 ? 'periodo' : 'periodos'}
                                            </span>
                                        )}
                                    </div>

                                    {details.plan_info?.subscription_periods && details.plan_info.subscription_periods.length > 0 ? (

                                        <div className="space-y-3 max-h-96 overflow-y-auto">
                                            {details.plan_info.subscription_periods.map((period: any, index: number) => (
                                                <div key={index} className="bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold text-sm">
                                                                {details.plan_info.subscription_periods.length - index}
                                                            </div>
                                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${period.plan === 'unlimited' ? 'bg-green-100 text-green-800' :
                                                                period.plan === 'premium_3000' ? 'bg-indigo-100 text-indigo-800' :
                                                                    period.plan === 'standard_1000' ? 'bg-purple-100 text-purple-800' :
                                                                        period.plan === 'basic_200' ? 'bg-blue-100 text-blue-800' :
                                                                            'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {period.plan_display}
                                                            </span>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-xs text-gray-500">Duración</div>
                                                            <div className="text-xs font-medium text-gray-700">
                                                                {(() => {
                                                                    const start = new Date(period.start_date);
                                                                    const end = new Date(period.end_date);
                                                                    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                                                                    return `${days} días`;
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                                        <div className="bg-gray-50 rounded p-2">
                                                            <div className="text-xs text-gray-500 mb-1">📅 Inicio</div>
                                                            <div className="text-xs font-medium text-gray-900">
                                                                {new Date(period.start_date).toLocaleString('es-ES', {
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </div>
                                                        </div>
                                                        <div className="bg-gray-50 rounded p-2">
                                                            <div className="text-xs text-gray-500 mb-1">🏁 Fin</div>
                                                            <div className="text-xs font-medium text-gray-900">
                                                                {new Date(period.end_date).toLocaleString('es-ES', {
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                                                        <div className="bg-indigo-50 rounded p-2">
                                                            <div className="font-bold text-indigo-600">{period.limit || '∞'}</div>
                                                            <div className="text-gray-500">Límite</div>
                                                        </div>
                                                        <div className="bg-green-50 rounded p-2">
                                                            <div className="font-bold text-green-600">{period.usage}</div>
                                                            <div className="text-gray-500">Usados</div>
                                                        </div>
                                                        <div className="bg-gray-50 rounded p-2">
                                                            <div className={`font-bold ${period.remaining === null ? 'text-green-600' : period.remaining <= 0 ? 'text-red-600' : 'text-indigo-600'}`}>
                                                                {period.remaining === null ? '∞' : period.remaining}
                                                            </div>
                                                            <div className="text-gray-500">Restantes</div>
                                                        </div>
                                                    </div>

                                                    {period.limit && (
                                                        <div className="mt-3">
                                                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                                                <span>Uso del periodo</span>
                                                                <span className="font-medium">{Math.round((period.usage / period.limit) * 100)}%</span>
                                                            </div>
                                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full transition-all ${period.remaining <= 0 ? 'bg-red-500' :
                                                                        period.remaining < (period.limit * 0.2) ? 'bg-yellow-500' :
                                                                            'bg-green-500'
                                                                        }`}
                                                                    style={{ width: `${Math.min(100, (period.usage / period.limit) * 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <Calendar className="h-8 w-8 text-gray-400" />
                                            </div>
                                            <p className="text-sm text-gray-600 font-medium">Sin periodos anteriores</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Los periodos se crearán cuando cambies de plan
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* 1. Lifetime Activity Bar */}
                                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-5 w-5 text-indigo-600" />
                                            <h4 className="font-semibold text-gray-900 capitalize">
                                                {selectedMonth === 'all' ? 'Actividad Total de la Cuenta' : `Actividad por Ciclo: ${selectedMonth}`}
                                            </h4>
                                        </div>

                                        {/* Month Selector */}
                                        {/* Month Selector */}
                                        <div style={{ width: '220px' }}>
                                            <ColumnSelect
                                                options={[
                                                    "Total Histórico",
                                                    ...(details?.billing_cycles ? Object.keys(details.billing_cycles).sort((a, b) => {
                                                        const nA = parseInt(a.replace(/\D/g, '')) || 0;
                                                        const nB = parseInt(b.replace(/\D/g, '')) || 0;
                                                        return nB - nA;
                                                    }) : [])
                                                ]}
                                                value={selectedMonth === 'all' ? "Total Histórico" : selectedMonth}
                                                onChange={(val: string) => setSelectedMonth(val === "Total Histórico" ? "all" : val)}
                                                placeholder="Selecciona Periodo"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-between text-sm text-gray-500 mb-2">
                                        <span>{dateLabelStart}: {dateValueStart}</span>
                                        <span>{dateLabelEnd}: {dateValueEnd}</span>
                                    </div>

                                    <div className="relative pt-1">
                                        <div className="flex mb-2 items-center justify-between">
                                            <div className="flex gap-4">
                                                <span className="text-xs font-semibold inline-block text-green-600">
                                                    ✔ Entregados (plan): {displayMessages.delivered}
                                                </span>
                                                <span className="text-xs font-semibold inline-block text-yellow-800">
                                                    ⚠ Pendientes: {displayMessages.pending}
                                                </span>
                                                <span className="text-xs font-semibold inline-block text-red-600">
                                                    ✖ Fallidos: {displayMessages.failed}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs font-semibold inline-block text-indigo-600">
                                                    Total: {displayMessages.total} mensajes
                                                </span>
                                            </div>
                                        </div>

                                        {/* Split Progress Bar */}
                                        <div className="overflow-hidden h-3 mb-4 text-xs flex rounded-full bg-gray-200">
                                            {/* Delivered Segment (Green) */}
                                            <div
                                                style={{ width: `${percentDelivered}%` }}
                                                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                                                title={`Enviados: ${displayMessages.delivered}`}
                                            ></div>

                                            {/* Pending Segment (Yellow) */}
                                            <div
                                                style={{ width: `${percentPending}%` }}
                                                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500"
                                                title={`Pendientes: ${displayMessages.pending}`}
                                            ></div>

                                            {/* Failed Segment (Red) */}
                                            <div
                                                style={{ width: `${percentFailed}%` }}
                                                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-red-500"
                                                title={`Fallidos: ${displayMessages.failed}`}
                                            ></div>
                                        </div>
                                        <p className="text-xs text-gray-400 text-center">Datos acumulados desde la creación de la cuenta hasta hoy.</p>
                                    </div>
                                </div>

                            </div>
                        ) : null}
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button type="button" onClick={onClose} className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, count, color, icon }: any) => {
    const colors: any = {
        green: "bg-green-50 text-green-700 border-green-200",
        yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        red: "bg-red-50 text-red-700 border-red-200",
    };

    return (
        <div className={`p-3 rounded-lg border ${colors[color] || colors.blue} flex flex-col items-center justify-center text-center`}>
            <div className="mb-1 opacity-80">{icon}</div>
            <span className="text-2xl font-bold">{count}</span>
            <span className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</span>
        </div>
    );
};
